import { getCookie } from "cookies-next/client";
import type { PostHog } from "posthog-js";

/**
 * Campaign parameters posthog-js lifts off the landing URL and stamps onto
 * every subsequent event. Mirrors CAMPAIGN_PARAMS in posthog-js
 * (src/utils/event-utils.ts) — keep in sync when the SDK adds an ad network,
 * otherwise that network's click ID stops surviving the consent transition
 * handled in `performance-consent.ts`.
 */
const CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gad_source",
  "mc_cid",
  // Ad-network click IDs (PERSONAL_DATA_CAMPAIGN_PARAMS upstream).
  "gclid",
  "gclsrc",
  "dclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "twclid",
  "li_fat_id",
  "igshid",
  "ttclid",
  "rdt_cid",
  "epik",
  "qclid",
  "sccid",
  "irclid",
  "_kx",
  "oppref",
] as const;

/**
 * Click IDs posthog-js also reads from a first-party cookie when they are
 * absent from the URL. The URL value wins when both are present.
 */
const COOKIE_CAMPAIGN_PARAMS = ["li_fat_id"] as const;

export type CampaignParams = Record<string, string | null>;

/**
 * Snapshots the campaign params on the landing URL in the shape posthog-js
 * registers them: every key present, `null` when absent, so re-registering
 * overwrites a stale value instead of leaving it behind.
 *
 * Read once at init rather than at consent time — consent can be granted after
 * an SPA navigation has already dropped the UTMs from the URL.
 */
export function readCampaignParams(
  url: string = window.location.href
): CampaignParams {
  const params: CampaignParams = {};
  let searchParams: URLSearchParams;
  try {
    searchParams = new URL(url).searchParams;
  } catch {
    return params;
  }
  // An empty value is "absent", not a value — the SDK normalises both the URL
  // and the cookie the same way (`kw ? kw : null`). Keeping "" would make
  // hasCampaignParams true and register a meaningless empty param on every
  // event, since hasCampaignParams only treats null as absent.
  for (const key of CAMPAIGN_PARAMS) {
    params[key] = searchParams.get(key) || null;
  }
  for (const key of COOKIE_CAMPAIGN_PARAMS) {
    params[key] ??= getCookie(key) || null;
  }
  return params;
}

export function hasCampaignParams(params: CampaignParams): boolean {
  return Object.values(params).some((value) => value !== null);
}

/**
 * Re-registers the landing URL's campaign params after a consent transition.
 *
 * `opt_in_capturing()` / `opt_out_capturing()` call `reset()` when they cross
 * the cookieless boundary, and that clears `sessionPersistence` — where
 * posthog-js keeps the `utm_*` and click-ID properties it copies onto every
 * event. The SDK repopulates them at most once per page load
 * (`update_campaign_params()` latches a private flag that `clear()` does not
 * reset), so without this every event after the transition carries no campaign
 * attribution at all.
 */
export function restoreCampaignParams(
  posthog: PostHog,
  params: CampaignParams
): void {
  // Matches the SDK, which skips registration entirely when the landing URL
  // carried no campaign params, rather than writing a row of nulls.
  if (!hasCampaignParams(params)) {
    return;
  }
  posthog.sessionPersistence?.register(params);
}
